import { useContext, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { LanguageContext } from "../../../Context/changeLanguageProvider";
import {
  DEMO_PATH,
  customerPath,
  useCanManageCustomers,
  usePartnerView,
} from "../PartnerNav/partnerNav";
import { UserData } from "../../../Context/DataContext";
import config from "../../../config";
import { submitCaptcha } from "../../../Utils/captcha";
import ReferralCodesSection from "../My Account/referralCodesSection";
import { syncScrollLeft } from "./scrollSync";
import { useDragScroll } from "../../../Utils/dragScroll";
import styles from "./styles/MyCustomers.module.css";

const base_url = config.base_url;

/*
  The partner's customer list — the "My Customer" side of the header switcher.

  Presentation only for now. The rows below are the ones drawn in the design
  (customer_tab.svg); nothing here talks to the backend yet, and the controls
  are inert on purpose rather than half-wired. What the real data will be is a
  separate conversation: the fields shown map onto things that do exist
  (ReferralEvents for invited-vs-created, UserData for name/phone/email,
  CustomerUser.user_status for the inheritance state), except "Inheritance
  triggered", which no backend code writes today.
*/


const SortIcon = () => (
  <svg className={styles.sort_icon} viewBox="0 0 12 14" aria-hidden="true">
    <path d="M3.5 1v12M3.5 13 1 10.5M8.5 13V1M8.5 1 11 3.5" />
  </svg>
);

const Avatar = () => (
  <svg className={styles.avatar} viewBox="0 0 32 32" aria-hidden="true">
    <circle cx="16" cy="16" r="16" fill="#E4E7EC" />
    <circle cx="16" cy="12.5" r="5" fill="#A3A9B5" />
    <path d="M6.5 29a9.5 9.5 0 0 1 19 0z" fill="#A3A9B5" />
  </svg>
);

export default function MyCustomers() {
  /*
    Partners only. The switcher is the only way in, but the route answers to
    anyone who types it, and a customer has no customers to list -- so send them
    back rather than show an empty stranger of a page.
  */
  const partnerView = usePartnerView();
  const canManageCustomers = useCanManageCustomers();
  const navigate = useNavigate();
  const userData = UserData();
  const websiteCurrentLang = useContext(LanguageContext).language;

  /*
    The invite block lives here now rather than on the Partner Portal tab. It is
    the same ReferralCodesSection component, unchanged, so the invite call, the
    daily-limit handling and the referral-link copy all behave exactly as they
    did.

    It needs two things. referralData and its setter come straight off
    DataContext, which already loads /user/referral/data for every user page.
    masterCode is the partner's primary referral code, which the portal derives
    from /user/referral/info -- the same request, made the same way, is repeated
    here.
  */
  /*
    The customer list. Everything in it is readable by the server -- name, phone,
    email and both statuses are encrypted at rest under Ostreon's key, not the
    customer's -- so this needs no key material and works even for customers who
    have never signed in since being referred.
  */
  const [customers, setCustomers] = useState(null);
  /*
    Bumped when an invitation is sent, which re-runs the load below.

    The invite block posts to a different endpoint than this table reads, so
    a new invitation left no trace here until the page was reloaded by hand.
  */
  const [reloadKey, setReloadKey] = useState(0);
  // The invite block at the foot of the page; the invite button scrolls to it.
  const invitePanelRef = useRef(null);

  // The headings and the rows scroll sideways as one -- see scrollSync.js.
  const headScrollRef = useRef(null);
  const rowsScrollRef = useRef(null);
  /*
    And both can be grabbed and dragged, as the Asset Management table can.

    Below 1000px these are the only way across the table on a machine with no
    horizontal wheel -- the scrollbar is the bar at the bottom of a card and
    nothing else moved it. Each box is given the gesture rather than only the
    rows: whichever is dragged, the other follows through the scroll handlers
    already wired above.
  */
  useDragScroll(headScrollRef);
  useDragScroll(rowsScrollRef);
  useEffect(() => {
    let live = true;
    fetch(base_url + "user/partner/customers", {
      method: "POST",
      mode: "cors",
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-type": "application/x-www-form-urlencoded",
        credentials: "include",
      },
      credentials: "include",
      body: ``,
    })
      .then((res) => (res.status === 200 ? res.json() : { customers: [] }))
      .then((data) => {
        if (live) setCustomers(data?.customers || []);
      })
      .catch((err) => {
        console.log(err);
        if (live) setCustomers([]);
      });
    return () => {
      live = false;
    };
  }, [reloadKey]);

  const [masterCode, setMasterCode] = useState([]);
  useEffect(() => {
    let live = true;
    fetch(base_url + "user/referral/info", {
      method: "POST",
      mode: "cors",
      headers: {
        Accept: "application/json, text/plain, /",
        "Content-type": "application/x-www-form-urlencoded",
        credentials: "include",
      },
      credentials: "include",
      body: ``,
    })
      .then((res) => res.json())
      .then((data) => {
        // Guarded: an empty list is a partner with no codes yet, not an error.
        if (live && Array.isArray(data)) {
          setMasterCode(data.filter((code) => code.master === true));
        }
      })
      .catch((err) => console.log(err));
    return () => {
      live = false;
    };
  }, []);
  const [text, setText] = useState(websiteCurrentLang.content.DublicatedText);
  useEffect(() => {
    setText(websiteCurrentLang.content.DublicatedText);
  }, [websiteCurrentLang]);

  const t = (key) => text?.[key] || "";

  /*
    ---- resending an invitation ----

    Only invited rows carry an `invitation_id`, and it is the id of the
    referral event the invitation was sent as. The server re-sends to the
    address held on that event -- nothing here can redirect it -- and it
    refuses an event that is not this partner's, or one emailed within the
    last day (Middleware/referral.js, mwCodeisOwned).

    Per row, because a partner with a page of invitations will send several
    and each button has to speak for itself.
  */
  const [resent, setResent] = useState({});
  const mark = (id, state) => setResent((prev) => ({ ...prev, [id]: state }));

  const resendInvitation = async (invitationId) => {
    if (!invitationId || resent[invitationId] === "sending") return;
    mark(invitationId, "sending");
    /*
      The invite endpoint does not check this token today, but both of the
      other callers send one and it would be the first thing to add. Raced
      against a timeout because submitCaptcha polls for grecaptcha forever: if
      Google's script is blocked, the button would otherwise spin for good.
    */
    let token = "";
    try {
      token = await Promise.race([
        submitCaptcha(),
        new Promise((resolve) => setTimeout(() => resolve(""), 4000)),
      ]);
    } catch (e) {
      token = "";
    }
    try {
      const res = await fetch(base_url + "user/referral/invite", {
        method: "POST",
        mode: "cors",
        body: `id=${encodeURIComponent(invitationId)}&token=${encodeURIComponent(
          token || ""
        )}`,
        headers: {
          Accept: "application/json, text/plain, */*",
          "Content-type": "application/x-www-form-urlencoded",
        },
        credentials: "include",
      });
      /*
        A rejected address comes back 200 with an error in the body -- the
        mail server refused it, which is a failure however it is dressed.
      */
      const body = await res.json().catch(() => null);
      mark(invitationId, res.status === 200 && !body?.error ? "sent" : "failed");
    } catch (e) {
      mark(invitationId, "failed");
    }
  };

  const EMPTY_CELL = "-";

  /*
    ---- search ----

    Name, phone and email: the three things a partner knows about a customer
    before they open one. Not the statuses -- those are a handful of repeated
    words, and typing one would select most of the page rather than narrow it.

    Phone numbers are matched twice over. Once as written, so "+33" finds a
    number that starts with it, and once as bare digits on both sides, so
    "0612" finds "+33 6 12 34 56" -- a stored number carries whatever spacing
    the customer typed, and nobody searching for a phone number types the
    spaces.

    An invited row has only an address, which is exactly what a partner would
    search for to find one.
  */
  const [search, setSearch] = useState("");
  const digitsOf = (value) => String(value || "").replace(/[^0-9]/g, "");
  const matchesSearch = (row) => {
    const needle = search.trim().toLowerCase();
    if (!needle) return true;
    const fields = [row?.name, row?.email, row?.phone_number];
    if (fields.some((f) => String(f || "").toLowerCase().includes(needle)))
      return true;
    /*
      Leading zeros dropped from what was typed, so a number searched the way
      it is dialled at home finds one stored the way it is dialled from
      abroad: "0612" finds "+33 6 12 34 56", where the trunk 0 gives way to
      the country code and is simply not in the stored digits.
    */
    const typed = digitsOf(needle).replace(/^0+/, "");
    return typed !== "" && digitsOf(row?.phone_number).includes(typed);
  };

  const withPlus = (number) => {
    const digits = String(number || "").trim();
    if (!digits) return EMPTY_CELL;
    return digits.startsWith("+") ? digits : `+${digits}`;
  };

  if (!partnerView || !canManageCustomers)
    return <Navigate to={DEMO_PATH} replace />;

  /*
    Phone numbers are stored without a country prefix, so they read as a bare
    string of digits. The + is added here rather than stored, because it is
    presentation: the stored value stays exactly what the customer typed.

    A customer with no number gets the dash the Asset Management table uses for
    an empty cell, rather than nothing — a blank cell reads as a column that
    failed to load, which is exactly how the user status column read while it
    was broken. A lone "+" would be worse than either.

    A number that already carries a + is left alone rather than given a second.
  */

  /*
    The state of the customer's email address, as one lifecycle. The server
    decides which applies (Routes/PartnerUser.js, POST /partner/customers) and
    sends exactly one of these four.

    `created_invited` is the old value, kept mapped so a browser running this
    build against a server that has not been updated yet still shows something
    rather than a blank cell.
  */
  const userStatusLabel = {
    invited: t("dublicatedText86"),
    created: t("dublicatedText291"),
    deleted: t("deleted"),
    triggered: t("dublicatedText293"),
    created_invited: t("dublicatedText291"),
  };

  /*
    The three inheritance states the design shows. Active reads green, the other
    two red — "triggered" is not shouted in caps because it is a sentence rather
    than a state word, which is how the design sets it.
  */
  const inheritance = {
    active: { label: t("active"), className: styles.status_good, caps: true },
    inactive: { label: t("inactive"), className: styles.status_bad, caps: true },
    triggered: { label: t("dublicatedText293"), className: styles.status_bad, caps: false },
  };

  const columns = [
    t("dublicatedText288"),
    t("phone_number"),
    t("dublicatedText74"),
    t("dublicatedText289"),
    t("dublicatedText290"),
    t("view"),
  ];

  // What the search leaves. `customers` stays the full list the server sent.
  const shown = (customers || []).filter(matchesSearch);

  return (
    <div className={styles.page}>
      <h1 className={styles.page_title}>{t("dublicatedText285")}</h1>

      {/* Search and the invite action, above the column headings. */}
      <div
        className={styles.toolbar_card}
        ref={headScrollRef}
        onScroll={syncScrollLeft(headScrollRef, rowsScrollRef)}
      >
        <div className={styles.toolbar}>
          <div className={styles.search}>
            <svg className={styles.search_icon} viewBox="0 0 20 20" aria-hidden="true">
              <circle cx="9" cy="9" r="6.25" />
              <path d="M13.6 13.6 17.5 17.5" />
            </svg>
            <input
              className={styles.search_input}
              type="text"
              placeholder={t("dublicatedText65")}
              aria-label={t("dublicatedText65")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <button
            type="button"
            className={styles.invite_button}
            /*
              The invite block is already on this page, at the bottom. This takes
              the reader to it rather than opening anything -- there is nothing
              to open, and before this the button did nothing at all.
            */
            onClick={() =>
              invitePanelRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
              })
            }
          >
            <svg className={styles.plus} viewBox="0 0 16 16" aria-hidden="true">
              <path d="M8 2.5v11M2.5 8h11" />
            </svg>
            {t("dublicatedText287")}
          </button>
        </div>

        <div className={`${styles.row_grid} ${styles.head_row}`}>
          {columns.map((label) => (
            <div key={label} className={styles.head_cell}>
              <span>{label}</span>
              <SortIcon />
            </div>
          ))}
        </div>
      </div>

      <div
        className={styles.rows}
        ref={rowsScrollRef}
        onScroll={syncScrollLeft(rowsScrollRef, headScrollRef)}
      >
        {/*
          Two different nothings. An account with no customers at all is told
          so; a search that matched none of them is told THAT, because the
          first message would read as the list having emptied itself.
        */}
        {customers !== null && shown.length === 0 && (
          <div className={`${styles.row} ${styles.empty_row}`}>
            {search.trim() ? t("dublicatedText304") : t("dublicatedText298")}
          </div>
        )}
        {shown.map((row) => {
          const state = inheritance[row.inheritance_status] || inheritance.inactive;
          // An invitation that was never taken up: a row with no account.
          const noAccount = !row.user_id;
          return (
            <div
              key={row.user_id || `invitation:${row.invitation_id}`}
              className={`${styles.row_grid} ${styles.row}`}
            >
              <div className={`${styles.cell} ${styles.name_cell}`}>
                <Avatar />
                {/*
                  A dash rather than nothing when there is no name. An invited
                  row has only an address -- the name is the customer's own,
                  and they have not written one yet -- and a blank cell reads
                  as a column that failed to load, which is how the user
                  status column read before it was fixed. The phone beside it
                  says the same thing the same way.
                */}
                <span className={styles.name}>
                  {String(row.name || "").trim() || EMPTY_CELL}
                </span>
              </div>
              <div className={styles.cell}>{withPlus(row.phone_number)}</div>
              <div className={styles.cell}>{row.email}</div>
              {/*
                `user_status`, not `userStatus` — that is the name the server
                sends (Routes/PartnerUser.js, POST /partner/customers). The
                camelCase spelling read undefined on every row, which is why
                this column was blank for everyone.
              */}
              <div className={`${styles.cell} ${styles.status_cell}`}>
                <span>{userStatusLabel[row.user_status] || EMPTY_CELL}</span>
                {/*
                  Invited means the address was sent an invitation and nobody
                  has signed up with it. The only thing to do with such a row
                  is nudge it, so the nudge is here, beside the word.
                */}
                {row.user_status === "invited" && row.invitation_id && (
                  resent[row.invitation_id] === "sent" ? (
                    <span className={styles.resend_done}>
                      {t("dublicatedText300")}
                    </span>
                  ) : (
                    <button
                      type="button"
                      className={styles.resend_button}
                      /*
                        The server will refuse a second send inside a day, so
                        the button says so instead of letting the partner find
                        out from a failure.
                      */
                      disabled={
                        row.can_resend === false ||
                        resent[row.invitation_id] === "sending"
                      }
                      title={
                        row.can_resend === false
                          ? t("dublicatedText302")
                          : undefined
                      }
                      onClick={() => resendInvitation(row.invitation_id)}
                    >
                      {resent[row.invitation_id] === "failed"
                        ? t("dublicatedText301")
                        : t("dublicatedText299")}
                    </button>
                  )
                )}
              </div>
              {/*
                An invited row has no account behind it, so it has no
                inheritance state either -- and the fallback here is
                "INACTIVE", which would be a claim about an account that does
                not exist. A dash is what this column means when there is
                nothing to report.
              */}
              {noAccount ? (
                <div className={styles.cell}>{EMPTY_CELL}</div>
              ) : (
                <div className={`${styles.cell} ${state.className}`}>
                  {state.caps ? String(state.label).toUpperCase() : state.label}
                </div>
              )}
              {/*
                The way in, for every row that has an account behind it.

                It used to appear only when the customer had shared something,
                and the other two states said why there was nothing to open.
                But a partner may add an asset or a beneficiary for a customer
                to approve, and a customer who has shared nothing is the one
                most likely to need that -- so the door is open from the
                moment the account exists, and what used to be the reason for
                locking it is now a note beside the button saying what to
                expect inside.
              */}
              <div className={`${styles.cell} ${styles.action_cell}`}>
                {noAccount ? (
                  // No account yet: nothing to open, and nothing to say.
                  <span className={styles.view_note}>{EMPTY_CELL}</span>
                ) : (
                  <>
                    <button
                      type="button"
                      className={styles.edit_button}
                      aria-label={t("view")}
                      onClick={() => navigate(customerPath(row.user_id))}
                    >
                      <svg viewBox="0 0 18 18" aria-hidden="true">
                        <path d="M12.1 2.3a1.6 1.6 0 0 1 2.3 0l1.3 1.3a1.6 1.6 0 0 1 0 2.3l-8.4 8.4-3.9.9.9-3.9z" />
                      </svg>
                    </button>
                    {row.view !== "shared" && (
                      <span className={styles.view_note}>
                        {row.view === "no_data"
                          ? t("dublicatedText297")
                          : t("dublicatedText296")}
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* The invite block, moved here from the Partner Portal tab. */}
      <div className={styles.invite_panel} ref={invitePanelRef}>
        <ReferralCodesSection
          referralData={userData?.referralData}
          setReferralData={userData?.setReferralData}
          masterCode={masterCode}
          onInvited={() => setReloadKey((key) => key + 1)}
        />
      </div>
    </div>
  );
}
